'use client'

import { INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { useDirectoryContext } from '@/features/drive/entities/directory/directory-context'
import {
    IFileUploadState,
    IUseDirectoryFileUploadParams,
    useDirectoryFileUpload,
} from '@/features/drive/functionalities/upload/utils/use-directory-file-upload'
import { IChildrenProps } from '@/utils/typings/children-props'
import { createContext, useContext } from 'react'

interface IDirectoryFileUploadContext {
    fileUploadState: IFileUploadState
    uploadFiles: undefined | ((files: File[]) => Promise<INode[] | undefined>)
    openNativeFileUpload: undefined | (() => void)
    config: IPartialFileUploadConfig
}

const DirectoryFileUploadContext = createContext<IDirectoryFileUploadContext>({
    fileUploadState: {
        state: 'idle',
        id: Date.now(),
    },
    uploadFiles: undefined,
    openNativeFileUpload: undefined,
    config: {},
})

type IPartialFileUploadConfig = Pick<
    IUseDirectoryFileUploadParams,
    'accept' | 'setIsLoading' | 'onUpload' | 'productUuid' | 'multiple' | 'getPayload' | 'onUploadStateChange'
>

export type IDirectoryFileUploadContextProviderProps = IChildrenProps & IPartialFileUploadConfig

export const DirectoryFileUploadContextProvider = ({
    children,
    ...config
}: IDirectoryFileUploadContextProviderProps) => {
    const {
        nodeSource,
        nodePath,
        isPreview,
        content: { nodeContentQueryKey },
        implementation,
    } = useDirectoryContext()

    const { fileUploadNodes, openFileUpload, uploadFiles, fileUploadState } = useDirectoryFileUpload({
        path: nodePath,
        nodeContentQueryKey,
        implementation,
        ...config,
    })

    return (
        <DirectoryFileUploadContext.Provider
            value={{
                fileUploadState,
                uploadFiles: isPreview ? undefined : nodeSource === 'drive' ? uploadFiles : undefined,
                openNativeFileUpload: nodeSource === 'drive' ? openFileUpload : undefined,
                config,
            }}
        >
            {fileUploadNodes}

            {children}
        </DirectoryFileUploadContext.Provider>
    )
}

export const useDirectoryFileUploadContext = () => useContext(DirectoryFileUploadContext)
